import React, { useEffect, useMemo, useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Card, CardContent } from './ui/card';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Building2, CheckCircle2, ChevronDown, ChevronRight, Clock, Users, UserX } from 'lucide-react';
import { TasksByClient } from './TasksByClient';

const PRIORITY_RANK = { high: 0, medium: 1, low: 2 };

const sortPending = (list) =>
  [...list].sort((a, b) => {
    const da = a.due_date ? new Date(a.due_date).getTime() : Infinity;
    const db = b.due_date ? new Date(b.due_date).getTime() : Infinity;
    if (da !== db) return da - db;
    return (PRIORITY_RANK[a.priority] ?? 1) - (PRIORITY_RANK[b.priority] ?? 1);
  });

const sortCompleted = (list) =>
  [...list].sort(
    (a, b) =>
      new Date(b.completed_at || b.updated_at || b.created_at || 0) -
      new Date(a.completed_at || a.updated_at || a.created_at || 0),
  );

const pendingCount = (list) => list.filter((t) => t.status !== 'completed').length;

function CountPill({ n, testId }) {
  return (
    <span
      data-testid={testId}
      className={`ml-1.5 inline-flex min-w-[20px] justify-center rounded-full px-1.5 py-0.5 text-[11px] font-bold leading-none ${
        n > 0 ? 'bg-amber-100 text-amber-800' : 'bg-muted text-muted-foreground'
      }`}
    >
      {n}
    </span>
  );
}

export function PersonTaskSections({ tasks, renderRow, testKey, initialClient = null }) {
  const [showDone, setShowDone] = useState(false);
  const pending = useMemo(() => sortPending(tasks.filter((t) => t.status !== 'completed')), [tasks]);
  const done = useMemo(() => sortCompleted(tasks.filter((t) => t.status === 'completed')), [tasks]);

  if (tasks.length === 0) {
    return <p className="px-4 py-8 text-center text-sm text-muted-foreground" data-testid={`empty-${testKey}`}>Sin tareas</p>;
  }

  return (
    <div data-testid={`sections-${testKey}`}>
      <div
        className="flex items-center gap-2 bg-muted/40 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground"
        data-testid={`pending-header-${testKey}`}
      >
        <Clock className="h-3.5 w-3.5 text-amber-500" /> Pendientes ({pending.length})
      </div>
      {pending.length === 0 ? (
        <p className="px-4 py-5 text-sm text-muted-foreground">Sin tareas pendientes</p>
      ) : (
        <div data-testid={`pending-list-${testKey}`}>
          <TasksByClient tasks={pending} renderRow={renderRow} testKey={testKey} initialClient={initialClient} />
        </div>
      )}

      <button
        type="button"
        onClick={() => setShowDone((v) => !v)}
        aria-expanded={showDone}
        className="flex w-full items-center gap-2 border-t bg-muted/40 px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground transition-colors hover:bg-muted/70"
        data-testid={`done-toggle-${testKey}`}
      >
        {showDone ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" /> Realizadas ({done.length}) <span className="normal-case font-normal opacity-70">· últimos 7 días, después se archivan</span>
      </button>
      {showDone &&
        (done.length === 0 ? (
          <p className="border-t px-4 py-5 text-sm text-muted-foreground">Ninguna tarea realizada</p>
        ) : (
          <div className="divide-y border-t" data-testid={`done-list-${testKey}`}>{done.map(renderRow)}</div>
        ))}
    </div>
  );
}

function GroupedView({ groups, renderRow, prefix }) {
  return (
    <Card>
      <CardContent className="divide-y-4 divide-muted p-0">
        {groups.map((g) => (
          <section key={g.key} data-testid={`${prefix}-group-${g.key}`}>
            <header className="flex items-center gap-3 border-b bg-background px-4 py-3">
              <Avatar className="h-7 w-7">
                <AvatarFallback className="text-[10px] font-semibold">{g.avatar || <g.Icon className="h-3.5 w-3.5" />}</AvatarFallback>
              </Avatar>
              <h3 className="font-semibold">{g.name}</h3>
              <span className="text-xs text-muted-foreground">
                {pendingCount(g.tasks)} pendientes · {g.tasks.length - pendingCount(g.tasks)} realizadas
              </span>
            </header>
            <PersonTaskSections tasks={g.tasks} renderRow={renderRow} testKey={`${prefix}-${g.key}`} />
          </section>
        ))}
      </CardContent>
    </Card>
  );
}

export function CeoTasksByPerson({ tasks, people, renderRow, initialTab, initialClient = null }) {
  const [tab, setTab] = useState(initialTab || 'all');
  useEffect(() => { if (initialTab) setTab(initialTab); }, [initialTab]);

  const { groups, teamGroups } = useMemo(() => {
    const ids = new Set(people.map((p) => p.id));
    const byPerson = new Map(people.map((p) => [p.id, []]));
    const clients = [];
    const unassigned = [];
    for (const t of tasks) {
      if (t.assigned_to && ids.has(t.assigned_to)) byPerson.get(t.assigned_to).push(t);
      else if (t.client_id) clients.push(t);
      else unassigned.push(t);
    }
    const personGroups = people.map((p) => ({ key: p.id, name: p.name, avatar: p.avatar, role: p.role, tasks: byPerson.get(p.id) }));
    const list = [...personGroups];
    if (clients.length) list.push({ key: 'clients', name: 'Clientes', avatar: null, Icon: Building2, tasks: clients });
    if (unassigned.length) list.push({ key: 'unassigned', name: 'Sin asignar', avatar: null, Icon: UserX, tasks: unassigned });
    return { groups: list, teamGroups: personGroups.filter((g) => g.role !== 'ceo') };
  }, [tasks, people]);

  // Si la pestaña activa ya no existe (p. ej. "Clientes" sin tareas), vuelve a "Todas"
  const validTabs = new Set(['all', 'team', ...groups.map((g) => g.key)]);
  const activeTab = validTabs.has(tab) ? tab : 'all';
  const teamTasks = teamGroups.flatMap((g) => g.tasks);

  return (
    <Tabs value={activeTab} onValueChange={setTab} data-testid="ceo-tasks-tabs">
      <TabsList className="flex h-auto flex-wrap justify-start gap-1 bg-muted/60 p-1">
        <TabsTrigger value="all" data-testid="ceo-tab-all">
          Todas <CountPill n={pendingCount(tasks)} testId="ceo-tab-count-all" />
        </TabsTrigger>
        <TabsTrigger value="team" data-testid="ceo-tab-team">
          <Users className="mr-1 h-3.5 w-3.5" /> Equipo <CountPill n={pendingCount(teamTasks)} testId="ceo-tab-count-team" />
        </TabsTrigger>
        {groups.map((g) => (
          <TabsTrigger key={g.key} value={g.key} data-testid={`ceo-tab-${g.key}`}>
            {g.Icon && <g.Icon className="mr-1 h-3.5 w-3.5" />}
            {g.name} <CountPill n={pendingCount(g.tasks)} testId={`ceo-tab-count-${g.key}`} />
          </TabsTrigger>
        ))}
      </TabsList>

      <TabsContent value="all" className="mt-4">
        <GroupedView groups={groups} renderRow={renderRow} prefix="all" />
      </TabsContent>
      <TabsContent value="team" className="mt-4">
        <GroupedView groups={teamGroups} renderRow={renderRow} prefix="team" />
      </TabsContent>

      {groups.map((g) => (
        <TabsContent key={g.key} value={g.key} className="mt-4">
          <Card>
            <CardContent className="p-0">
              <PersonTaskSections tasks={g.tasks} renderRow={renderRow} testKey={g.key} initialClient={initialClient} />
            </CardContent>
          </Card>
        </TabsContent>
      ))}
    </Tabs>
  );
}
